import React from 'react';
import { Trash2, X, Archive } from 'lucide-react';

interface DeleteConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onArchive?: () => void;
    isArchived?: boolean;
}

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    onArchive,
    isArchived = false
}) => {
    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    const handleArchive = () => {
        if (onArchive) {
            onArchive();
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleBackdropClick}
        >
            <div className="bg-slate-800 border border-gray-600 rounded-lg shadow-xl max-w-md w-full mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-600">
                    <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${isArchived ? 'bg-red-500/20' : 'bg-purple-500/20'}`}>
                            <Trash2 className="w-5 h-5 text-red-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">
                            Delete Bookmark
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-300 mb-4">
                        {isArchived
                            ? "Are you sure you want to delete this archived bookmark? This action cannot be undone."
                            : "Are you sure you want to delete this bookmark? This action cannot be undone."
                        }
                    </p>
                    {!isArchived && (
                        <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-4">
                            <div className="flex items-start space-x-3">
                                <div className="p-1 bg-purple-500/20 rounded-full mt-0.5">
                                    <Archive className="w-4 h-4 text-purple-400" />
                                </div>
                                <div>
                                    <p className="text-purple-300 font-medium text-sm mb-1">
                                        Alternative Option
                                    </p>
                                    <p className="text-purple-200 text-sm">
                                        You can archive this bookmark instead if you don't wish to see it on your timeline. Archived bookmarks can be viewed in the Archived Bookmarks section.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-600">
                    {isArchived && <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>}
                    {!isArchived && onArchive && (
                        <button
                            onClick={handleArchive}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                        >
                            <Archive className="w-4 h-4" />
                            <span>Archive</span>
                        </button>
                    )}
                    <button
                        onClick={handleConfirm}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete Permanently</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
